import * as THREE from 'three';
import { clamp01, smoothstep, easeOutCubic, easeInOutCubic, lerp } from './Anim.js';

/**
 * Procedural animation sequences. No imported clips: every phase is a normalised
 * window driving bone channels and the additive weapon offset.
 *
 * A sequence is `{ duration, step(t01, elapsed, vm, inst), onStart?, onEnd? }`.
 * `vm.seqOffset` is the additive weapon transform; bone channels are written through
 * `inst.translateBone` / `inst.rotateBone`, both of which layer on the bind pose.
 */

const AX = new THREE.Vector3(1, 0, 0);
const AY = new THREE.Vector3(0, 1, 0);
const AZ = new THREE.Vector3(0, 0, 1);
const _v = new THREE.Vector3();

/** Normalised progress inside a window, 0 before `a`, 1 after `b`. */
const win = (t, a, b) => clamp01((t - a) / Math.max(1e-6, b - a));
/** A 0 -> 1 -> 0 pulse across a window. */
const pulse = (t, a, b) => {
  const u = win(t, a, b);
  return Math.sin(u * Math.PI);
};

function curlFingers(inst, side, names, amount, from = 0) {
  for (const f of names) {
    for (let j = 0; j < 3; j++) {
      const w = [1.0, 0.85, 0.7][j];
      inst.rotateBone(`${side}_${f}${j}`, AZ, lerp(from, amount, 1) * w);
    }
  }
}

/* ------------------------------------------------------------------ *
 * Magazine reload
 * ------------------------------------------------------------------ */

/**
 * Tactical (2.1 s) and empty (2.9 s) magazine changes. The empty variant adds a
 * bolt-release beat at the end and holds the weapon longer.
 */
export function reloadSequence(inst, empty, events) {
  const def = inst.def;
  const D = empty ? (def.reloadEmptyTime ?? 2.90) : (def.reloadTime ?? 2.10);
  const magDrop = def.magDropDistance ?? 0.34;
  const handDown = def.magHandTravel ?? 0.30;
  const magBone = def.rig?.mag ?? 'mag';
  const armL = 'left_arm';
  let swapped = false;

  // key times as fractions of the tactical duration, rescaled for the empty variant
  const K = empty
    ? { tilt: 0.03, press: 0.10, drop: 0.14, out: 0.34, fetch: 0.30, up: 0.44,
        seat: 0.60, settle: 0.66, bolt: 0.74, boltEnd: 0.82, ret: 0.86 }
    : { tilt: 0.04, press: 0.12, drop: 0.16, out: 0.40, fetch: 0.34, up: 0.50,
        seat: 0.68, settle: 0.76, bolt: 1.1, boltEnd: 1.1, ret: 0.84 };

  return {
    duration: D,
    name: empty ? 'reload_empty' : 'reload',
    onStart(vm) {
      // `duration` is the real length of THIS reload — tactical and empty differ,
      // and they differ again per weapon. The HUD drives its reload arc from it
      // and self-closes on it, so it must be the truth, not a 2.1 s guess.
      events?.emit('weapon:reload', {
        weapon: inst.info(), weaponName: inst.name, phase: 'start',
        duration: D, empty: !!empty, kind: empty ? 'empty' : 'tactical',
      });
    },
    onEnd(vm) {
      events?.emit('weapon:reload', {
        weapon: inst.info(), weaponName: inst.name, phase: 'end',
        duration: D, empty: !!empty, kind: empty ? 'empty' : 'tactical',
      });
    },
    step(t, e, vm) {
      /* ---- weapon canted toward the support hand ---- */
      const tilt = smoothstep(win(t, 0, K.tilt + 0.10)) * (1 - smoothstep(win(t, K.ret, 1)));
      vm.seqOffset.rot.x += tilt * 0.20;
      vm.seqOffset.rot.y += tilt * 0.34;
      vm.seqOffset.rot.z += tilt * 0.30;
      vm.seqOffset.pos.x += tilt * 0.030;
      vm.seqOffset.pos.y += tilt * -0.028;
      vm.seqOffset.pos.z += tilt * 0.020;

      /* ---- magazine release ---- */
      const press = pulse(t, K.press, K.press + 0.06);
      if (def.rig?.magRelease) {
        inst.translateBone(def.rig.magRelease.bone, def.rig.magRelease.travel * press, 0, 0);
      }

      /* ---- old magazine falls free ---- */
      const out = win(t, K.drop, K.out);
      if (out > 0 && out < 1) {
        const f = out * out;
        inst.translateBone(magBone, 0, -magDrop * f, magDrop * 0.28 * f * f);
        inst.rotateBone(magBone, AX, -0.9 * f * f);
      } else if (out >= 1) {
        // off screen until the fresh magazine arrives
        inst.translateBone(magBone, 0, -1.2, 0);
      }

      /* ---- fresh magazine rides up with the support hand ---- */
      const up = win(t, K.up, K.seat);
      if (up > 0) {
        const f = easeOutCubic(up);
        const y = lerp(-magDrop * 1.35, 0, f);
        const z = lerp(magDrop * 0.30, 0, f);
        inst.translateBone(magBone, 0, y, z);
        inst.rotateBone(magBone, AX, lerp(0.55, 0, f));
      }
      /* ---- seat + settle bump ---- */
      const seat = pulse(t, K.seat, K.settle);
      inst.translateBone(magBone, 0, -0.004 * seat, 0);
      if (seat > 0.6) vm.recoilPos.kick(_v.set(0, -0.05 * seat * 0.02, 0.02));

      /* ---- support hand travel ---- */
      let hx = 0, hy = 0, hz = 0, hr = 0;
      const down = win(t, K.tilt, K.fetch);
      const back = win(t, K.up, K.seat);
      const home = win(t, K.settle, K.ret);
      const dwell = clamp01(down - back);
      hy -= handDown * (down - back * 0.98) * 1.0;
      hz += 0.11 * (down - back * 0.98);
      hx += 0.05 * (down - back * 0.98);
      hr += 0.9 * (down - back * 0.98);
      // after seating, the hand returns to the handguard
      const post = clamp01(back - home);
      hy -= 0.02 * post;
      inst.translateBone(armL, hx, hy, hz);
      inst.rotateBoneParent(armL, AX, hr * 0.5);
      // grip the fresh magazine
      const grab = clamp01(down * 1.4) * (1 - home);
      curlFingers(inst, 'left', ['index', 'middle', 'ring', 'pinky'], 0.30 * grab);

      /* ---- empty reload: bolt release ---- */
      if (empty) {
        const bolt = win(t, K.bolt, K.boltEnd);
        if (def.rig?.boltCatch && bolt > 0 && bolt < 1) {
          inst.translateBone(def.rig.boltCatch.bone, def.rig.boltCatch.travel * Math.sin(bolt * Math.PI), 0, 0);
        }
        // bolt is held to the rear until the catch is hit, then slams home
        if (def.rig?.bolt) {
          const held = t < K.bolt + 0.02 ? 1 : 1 - easeOutCubic(win(t, K.bolt + 0.02, K.bolt + 0.06));
          inst.translateBone(def.rig.bolt.bone, 0, 0, def.rig.bolt.travel * held);
          if (def.rig.dust) inst.rotateBone(def.rig.dust.bone, AZ, def.rig.dust.angle * held);
        }
        const slam = pulse(t, K.bolt + 0.02, K.bolt + 0.08);
        if (slam > 0.8) vm.recoilRot.kick(_v.set(0.6, 0, 0.3));
        // right hand nudges forward as the bolt is released
        inst.translateBone('right_arm', 0, 0, -0.004 * slam);
      }
    },
  };
}

/* ------------------------------------------------------------------ *
 * Pump action (shotgun)
 * ------------------------------------------------------------------ */

export function pumpSequence(inst) {
  const def = inst.def;
  const D = def.pumpTime ?? 0.62;
  const travel = def.rig?.pump?.travel ?? 0.075;
  return {
    duration: D,
    name: 'pump',
    step(t, e, vm) {
      const back = smoothstep(win(t, 0.05, 0.42));
      const fwd = smoothstep(win(t, 0.48, 0.90));
      const s = back - fwd;
      if (def.rig?.pump) inst.translateBone(def.rig.pump.bone, 0, 0, travel * s);
      if (def.rig?.bolt) inst.translateBone(def.rig.bolt.bone, 0, 0, (def.rig.bolt.travel ?? 0.05) * s);
      inst.translateBone('left_arm', 0, 0, travel * s);
      vm.seqOffset.pos.z += 0.012 * s;
      vm.seqOffset.rot.x += 0.045 * s;
      if (t > 0.88 && t < 0.94) vm.recoilRot.kick(_v.set(0.5, 0, 0.2));
    },
  };
}

/* ------------------------------------------------------------------ *
 * Shell-by-shell reload (shotgun)
 * ------------------------------------------------------------------ */

export function shellLoadSequence(inst, count, events) {
  const per = inst.def.shellLoadTime ?? 0.44;
  const D = per * count + 0.30;
  const payload = (phase) => ({
    weapon: inst.info(), weaponName: inst.name, phase,
    duration: D, shells: count, kind: 'shells',
  });
  return {
    duration: D,
    name: 'reload_shells',
    onStart() { events?.emit('weapon:reload', payload('start')); },
    onEnd() { events?.emit('weapon:reload', payload('end')); },
    step(t, e, vm) {
      const tilt = smoothstep(win(t, 0, 0.10)) * (1 - smoothstep(win(t, 0.90, 1)));
      vm.seqOffset.rot.z += tilt * 0.62;
      vm.seqOffset.rot.y += tilt * 0.22;
      vm.seqOffset.pos.y += tilt * -0.030;
      const cyc = (e % per) / per;
      const reach = Math.sin(clamp01(cyc / 0.5) * Math.PI);
      inst.translateBone('left_arm', 0.02 * reach, -0.16 * reach, 0.06 * reach);
      const push = Math.sin(clamp01((cyc - 0.55) / 0.25) * Math.PI);
      vm.seqOffset.pos.y += 0.004 * push;
    },
  };
}

/* ------------------------------------------------------------------ *
 * Melee
 * ------------------------------------------------------------------ */

export function meleeSequence(inst, events) {
  const D = inst.def.meleeTime ?? 0.68;
  const stock = inst.def.meleeStyle !== 'pistol';
  return {
    duration: D,
    name: 'melee',
    onStart() { events?.emit('weapon:melee', { weapon: inst.info(), weaponName: inst.name, duration: D }); },
    step(t, e, vm) {
      const wind = smoothstep(win(t, 0.0, 0.24));
      const strike = easeOutCubic(win(t, 0.26, 0.40));
      const back = smoothstep(win(t, 0.46, 1.0));
      const s = wind - strike * 1.75 + back * 0.75;
      if (stock) {
        vm.seqOffset.rot.x += -s * 0.55;
        vm.seqOffset.rot.y += s * 0.85;
        vm.seqOffset.rot.z += -s * 0.70;
        vm.seqOffset.pos.z += -s * 0.16;
        vm.seqOffset.pos.x += s * 0.10;
        vm.seqOffset.pos.y += s * 0.05;
      } else {
        vm.seqOffset.rot.y += s * 0.55;
        vm.seqOffset.rot.z += -s * 1.10;
        vm.seqOffset.pos.z += -s * 0.13;
        vm.seqOffset.pos.x += s * 0.06;
      }
      if (t > 0.38 && t < 0.44) vm.recoilRot.kick(_v.set(1.4, -0.8, 0.6));
    },
  };
}

/* ------------------------------------------------------------------ *
 * Inspect
 * ------------------------------------------------------------------ */

export function inspectSequence(inst) {
  const D = inst.def.inspectTime ?? 2.60;
  const def = inst.def;
  return {
    duration: D,
    name: 'inspect',
    step(t, e, vm) {
      // bring it up and rotate to show the left side, check the chamber, return
      const a = smoothstep(win(t, 0.02, 0.20));
      const b = smoothstep(win(t, 0.30, 0.48));
      const c = smoothstep(win(t, 0.58, 0.76));
      const d = smoothstep(win(t, 0.82, 1.0));
      const up = a - d;
      vm.seqOffset.pos.set(
        vm.seqOffset.pos.x - 0.055 * up + 0.02 * b,
        vm.seqOffset.pos.y + 0.045 * up,
        vm.seqOffset.pos.z + 0.075 * up,
      );
      vm.seqOffset.rot.x += 0.16 * up - 0.30 * c;
      vm.seqOffset.rot.y += -0.62 * (b - c) + 0.35 * up;
      vm.seqOffset.rot.z += 0.85 * (b - c) - 0.25 * up;

      // peek into the ejection port halfway through
      const peek = pulse(t, 0.44, 0.62);
      if (def.rig?.bolt) inst.translateBone(def.rig.bolt.bone, 0, 0, def.rig.bolt.travel * 0.45 * peek);
      if (def.rig?.dust) inst.rotateBone(def.rig.dust.bone, AZ, def.rig.dust.angle * peek);
      if (def.rig?.charging) inst.translateBone(def.rig.charging.bone, 0, 0, (def.rig.charging.travel ?? 0.06) * 0.45 * peek);
      inst.translateBone('left_arm', 0.02 * peek, 0.03 * peek, 0.06 * peek);
    },
  };
}

/* ------------------------------------------------------------------ *
 * Draw / holster
 * ------------------------------------------------------------------ */

export function drawSequence(inst) {
  const D = inst.def.drawTime ?? 0.48;
  return {
    duration: D,
    name: 'draw',
    step(t, e, vm) {
      const s = 1 - easeOutCubic(t);
      vm.seqOffset.pos.y += -0.16 * s;
      vm.seqOffset.pos.z += 0.06 * s;
      vm.seqOffset.pos.x += 0.05 * s;
      vm.seqOffset.rot.x += -1.05 * s;
      vm.seqOffset.rot.y += 0.42 * s;
      vm.seqOffset.rot.z += 0.30 * s;
    },
  };
}

export function holsterSequence(inst, onDone) {
  const D = inst.def.holsterTime ?? 0.26;
  return {
    duration: D,
    name: 'holster',
    onEnd() { onDone?.(); },
    step(t, e, vm) {
      const s = easeInOutCubic(t);
      vm.seqOffset.pos.y += -0.17 * s;
      vm.seqOffset.pos.z += 0.05 * s;
      vm.seqOffset.rot.x += -1.10 * s;
      vm.seqOffset.rot.y += 0.40 * s;
    },
  };
}
